import { create } from 'zustand'

const useBookingStore = create((set, get) => ({
  // Category
  categoryId:   null,
  categoryName: null,
  categorySlug: null,

  // Step 1 — Problem description
  description: '',

  // Step 2 — Location & access
  locationInStructure: '',
  heightFromFloor:     'ground',   // ground | 3-4m | 4-6m | 6m+
  scaffoldingNeeded:   'not_sure', // yes | no | not_sure
  accessNotes:         '',

  // Step 3 — Demolition & restoration
  demolitionInvolved:  'not_sure', // yes | no | not_sure
  demolitionElements:  [],         // ceiling | wall | floor_tiles | floor_slab | cabinetry | partition | other
  demolitionArea:      '',
  restorationNeeded:   'not_sure',
  paintRestoration:    'not_sure',

  // Step 4 — Work hours
  noiseAllowed:        'all_day',  // all_day | morning | afternoon | night
  workHours:           'regular',  // regular | extended | night_shift
  spaceOccupied:       'no',       // yes | no
  workRestrictions:    '',

  // Step 5 — Permits
  permitRequired:      'not_sure', // yes | no | not_sure
  clientHandlesPermit: 'not_sure', // yes | no | sk_handles
  permitNotes:         '',

  // Address
  address:      '',
  addressNotes: '',
  urgency:      'scheduled',

  // Photos
  photos: [], // { file, previewUrl }

  // After DB save
  bookingId:  null,
  bookingRef: null,
  quoteId:    null,

  // Current step (1-5)
  currentStep: 1,

  // ── Actions ──────────────────────────────────────────────────────────────────
  setCategory: ({ id, name, slug }) =>
    set({ categoryId: id, categoryName: name, categorySlug: slug }),

  setStep: (step) => set({ currentStep: step }),
  nextStep: () => set((s) => ({ currentStep: Math.min(s.currentStep + 1, 5) })),
  prevStep: () => set((s) => ({ currentStep: Math.max(s.currentStep - 1, 1) })),

  // Step 1
  setDescription: (v) => set({ description: v }),

  // Step 2
  setLocationInStructure: (v) => set({ locationInStructure: v }),
  setHeightFromFloor:     (v) => set({ heightFromFloor: v }),
  setScaffoldingNeeded:   (v) => set({ scaffoldingNeeded: v }),
  setAccessNotes:         (v) => set({ accessNotes: v }),

  // Step 3
  setDemolitionInvolved: (v) => set({ demolitionInvolved: v }),
  toggleDemolitionElement: (el) => set((s) => {
    const els = s.demolitionElements.includes(el)
      ? s.demolitionElements.filter((e) => e !== el)
      : [...s.demolitionElements, el]
    return { demolitionElements: els }
  }),
  setDemolitionArea:    (v) => set({ demolitionArea: v }),
  setRestorationNeeded: (v) => set({ restorationNeeded: v }),
  setPaintRestoration:  (v) => set({ paintRestoration: v }),

  // Step 4
  setNoiseAllowed:     (v) => set({ noiseAllowed: v }),
  setWorkHours:        (v) => set({ workHours: v }),
  setSpaceOccupied:    (v) => set({ spaceOccupied: v }),
  setWorkRestrictions: (v) => set({ workRestrictions: v }),

  // Step 5
  setPermitRequired:      (v) => set({ permitRequired: v }),
  setClientHandlesPermit: (v) => set({ clientHandlesPermit: v }),
  setPermitNotes:         (v) => set({ permitNotes: v }),

  // Address & urgency
  setAddress:      (v) => set({ address: v }),
  setAddressNotes: (v) => set({ addressNotes: v }),
  setUrgency:      (v) => set({ urgency: v }),

  // Photos
  addPhoto: (file) => {
    const previewUrl = URL.createObjectURL(file)
    set((s) => ({ photos: [...s.photos, { file, previewUrl }] }))
  },
  removePhoto: (index) => {
    const photos = get().photos
    URL.revokeObjectURL(photos[index]?.previewUrl)
    set({ photos: photos.filter((_, i) => i !== index) })
  },

  setBookingId: (id, ref) => set({ bookingId: id, bookingRef: ref }),
  setQuoteId:   (id)      => set({ quoteId: id }),

  // Build combined description string for Edge Function
  buildFullDescription: () => {
    const s = get()
    const lines = []

    lines.push('PROBLEM: ' + s.description)

    lines.push('\nLOCATION & ACCESS:')
    if (s.locationInStructure) lines.push('- Location: ' + s.locationInStructure)
    lines.push('- Height from floor: ' + s.heightFromFloor)
    lines.push('- Scaffolding needed: ' + s.scaffoldingNeeded)
    if (s.accessNotes) lines.push('- Access notes: ' + s.accessNotes)

    lines.push('\nDEMOLITION & RESTORATION:')
    lines.push('- Demolition involved: ' + s.demolitionInvolved)
    if (s.demolitionInvolved === 'yes' && s.demolitionElements.length > 0)
      lines.push('- Elements affected: ' + s.demolitionElements.join(', '))
    if (s.demolitionArea) lines.push('- Approximate area: ' + s.demolitionArea)
    lines.push('- Restoration needed: ' + s.restorationNeeded)
    lines.push('- Paint restoration needed: ' + s.paintRestoration)

    lines.push('\nWORK HOURS:')
    lines.push('- Noise allowed: ' + s.noiseAllowed)
    lines.push('- Work hours: ' + s.workHours)
    lines.push('- Space occupied during works: ' + s.spaceOccupied)
    if (s.workRestrictions) lines.push('- Other restrictions: ' + s.workRestrictions)

    lines.push('\nPERMITS:')
    lines.push('- Permit required: ' + s.permitRequired)
    lines.push('- Client handles permit: ' + s.clientHandlesPermit)
    if (s.permitNotes) lines.push('- Permit notes: ' + s.permitNotes)

    return lines.join('\n')
  },

  reset: () => {
    get().photos.forEach((p) => URL.revokeObjectURL(p.previewUrl))
    set({
      categoryId: null, categoryName: null, categorySlug: null,
      description: '', locationInStructure: '', heightFromFloor: 'ground',
      scaffoldingNeeded: 'not_sure', accessNotes: '',
      demolitionInvolved: 'not_sure', demolitionElements: [],
      demolitionArea: '', restorationNeeded: 'not_sure', paintRestoration: 'not_sure',
      noiseAllowed: 'all_day', workHours: 'regular', spaceOccupied: 'no',
      workRestrictions: '', permitRequired: 'not_sure',
      clientHandlesPermit: 'not_sure', permitNotes: '',
      address: '', addressNotes: '', urgency: 'scheduled',
      photos: [], bookingId: null, bookingRef: null, quoteId: null,
      currentStep: 1,
    })
  },
}))

export default useBookingStore
